<!-- src/components/clinical/ImageUploader.vue -->
<template>
    <div class="image-uploader">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">Subir Imágenes Clínicas</h5>
          
          <!-- Image drop zone -->
          <div 
            class="drop-zone" 
            :class="{ 'active': isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
            @click="triggerFileInput"
          >
            <div v-if="isDragging" class="drop-message">
              <i class="bi bi-cloud-arrow-down"></i>
              <p>Suelta los archivos aquí</p>
            </div>
            <div v-else class="drop-message">
              <i class="bi bi-cloud-upload"></i>
              <p>Arrastra imágenes aquí o haz clic para seleccionar</p>
            </div>
            
            <input 
              type="file" 
              ref="fileInput" 
              multiple
              accept="image/*" 
              class="file-input"
              @change="onFileSelected"
            >
          </div>
          
          <!-- File list -->
          <div v-if="files.length > 0" class="file-list mt-3">
            <div v-for="(file, index) in files" :key="index" class="file-item">
              <div class="file-preview">
                <img :src="file.preview" alt="Preview" class="img-thumbnail">
              </div>
              <div class="file-details">
                <div class="form-group mb-2">
                  <select v-model="file.imageType" class="form-select">
                    <option value="photo">Fotografía</option>
                    <option value="xray">Radiografía</option>
                    <option value="panoramic">Panorámica</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div class="form-group">
                  <input 
                    type="text" 
                    v-model="file.description" 
                    class="form-control" 
                    placeholder="Descripción (opcional)"
                  >
                </div>
              </div>
              <div class="file-actions">
                <button type="button" class="btn btn-sm btn-danger" @click="removeFile(index)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Upload button -->
          <div v-if="files.length > 0" class="mt-3 d-flex justify-content-end">
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="uploadFiles"
              :disabled="uploading"
            >
              <span v-if="uploading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Subir Imágenes
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { clinicalImageService } from '@/services/api'; // Import the service

  
  export default {
    name: 'ImageUploader',
    props: {
      clinicalHistoryId: {
        type: [Number, String],
        required: true
      }
    },
    data() {
      return {
        isDragging: false,
        files: [],
        uploading: false
      };
    },
    methods: {
      triggerFileInput() {
        this.$refs.fileInput.click();
      },
      onDrop(e) {
        this.isDragging = false;
        const droppedFiles = e.dataTransfer.files;
        this.handleFiles(droppedFiles);
      },
      onFileSelected(e) {
        const selectedFiles = e.target.files;
        this.handleFiles(selectedFiles);
      },
      handleFiles(fileList) {
        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i];
          
          if (!file.type.match('image.*')) {
            continue; // Skip non-image files
          }
          
          // Create file preview
          const reader = new FileReader();
          reader.onload = (e) => {
            this.files.push({
              file: file,
              preview: e.target.result,
              imageType: 'photo', // Default image type
              description: ''
            });
          };
          reader.readAsDataURL(file);
        }
      },
      removeFile(index) {
        this.files.splice(index, 1);
      },
      async uploadFiles() {
        if (this.files.length === 0) return;
        
        this.uploading = true;
        
        try {
          // Upload each file
          for (const fileObj of this.files) {
            const formData = new FormData();
            formData.append('image', fileObj.file);
            formData.append('clinical_history', this.clinicalHistoryId);
            formData.append('image_type', fileObj.imageType);
            formData.append('description', fileObj.description);
            
            await clinicalImageService.create(formData);
          }
          
          // Clear the file list
          this.files = [];
          
          // Emit success event
          this.$emit('upload-complete');
          
          // Show success message
          this.$store.dispatch('notification/success', 'Imágenes subidas correctamente');
        } catch (error) {
          console.error('Error uploading images:', error);
          this.$store.dispatch('notification/error', 'Error al subir las imágenes. Por favor, intenta de nuevo.');
        } finally {
          this.uploading = false;
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .drop-zone {
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 30px;
    text-align: center;
    cursor: pointer;
    transition: border 0.3s, background-color 0.3s;
  }
  
  .drop-zone.active {
    border-color: #007bff;
    background-color: #f0f8ff;
  }
  
  .drop-message i {
    font-size: 2.5rem;
    color: #6c757d;
    margin-bottom: 10px;
  }
  
  .drop-zone.active .drop-message i {
    color: #007bff;
  }
  
  .file-input {
    display: none;
  }
  
  .file-list {
    max-height: 300px;
    overflow-y: auto;
  }
  
  .file-item {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    padding: 10px;
    border: 1px solid #eee;
    border-radius: 5px;
  }
  
  .file-preview {
    width: 80px;
    height: 80px;
    margin-right: 15px;
    flex-shrink: 0;
  }
  
  .file-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .file-details {
    flex-grow: 1;
  }
  
  .file-actions {
    margin-left: 10px;
  }
  </style>